import { useEffect, useState } from "react";
import {
  FolderKanban,
  Users,
  TrendingUp,
} from "lucide-react";

import {
  getWorkspaceMembers,
  getWorkspaceItems,
} from "../services/workspaceService";

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Planning: "bg-yellow-100 text-yellow-700",
  Completed: "bg-blue-100 text-blue-700",
  "On Hold": "bg-red-100 text-red-700",
};

const WorkspaceHeader = ({
  workspace,
  project,
}) => {
  const [memberCount, setMemberCount] = useState(0);
  const [progress, setProgress] = useState(0);

  /*
   * Get workspace UUID
   */
  const workspaceUuid =
    workspace?.uuid ||
    workspace?.workspace_uuid ||
    workspace?.id;

  /*
   * ==============================
   * FETCH MEMBERS + ITEMS
   * ==============================
   */
  useEffect(() => {
    if (!workspaceUuid) return;

    const fetchWorkspaceData = async () => {
      /*
       * ------------------------------
       * Fetch Members
       * ------------------------------
       */
      try {
        const response =
          await getWorkspaceMembers(workspaceUuid);

        console.log(
          "WORKSPACE MEMBERS:",
          response
        );

        const members =
          Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        setMemberCount(members.length);
      } catch (error) {
        console.error(
          "FETCH WORKSPACE MEMBERS ERROR:",
          error
        );

        setMemberCount(0);
      }

      /*
       * ------------------------------
       * Fetch Workspace Items
       * ------------------------------
       */
      try {
        const response =
          await getWorkspaceItems(workspaceUuid);

        console.log(
          "WORKSPACE ITEMS:",
          response
        );

        const items =
          Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        /*
         * ------------------------------
         * Calculate Progress
         * ------------------------------
         */

        if (items.length === 0) {
          setProgress(0);
          return;
        }

        const completedItems = items.filter(
          (item) =>
            String(item.status || "")
              .toLowerCase()
              .trim() === "completed"
        ).length;

        const calculatedProgress = Math.round(
          (completedItems / items.length) * 100
        );

        setProgress(calculatedProgress);

        console.log(
          "TOTAL ITEMS:",
          items.length
        );

        console.log(
          "COMPLETED ITEMS:",
          completedItems
        );

        console.log(
          "CALCULATED PROGRESS:",
          calculatedProgress
        );
      } catch (error) {
        console.error(
          "FETCH WORKSPACE ITEMS ERROR:",
          error
        );

        setProgress(0);
      }
    };

    fetchWorkspaceData();
  }, [workspaceUuid]);

  /*
   * Prevent rendering without workspace
   */
  if (!workspace) return null;

  /*
   * Workspace information
   */
  const workspaceName =
    workspace.workspace_name ||
    workspace.name ||
    "Unnamed Workspace";

  const workspaceDescription =
    workspace.description || "";

  const workspaceCode =
    workspace.workspace_code ||
    workspace.workspaceCode ||
    "-";

  const workspaceStatus =
    workspace.status || "Planning";

  return (
    <div className="border-b border-slate-200 bg-white px-5 py-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">

          {/* ================= WORKSPACE NAME ================= */}

          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <FolderKanban
                size={20}
                className="text-blue-600"
              />
            </div>

            <div>
              <h2 className="text-lg font-bold text-slate-800">
                {workspaceName}
              </h2>

              {workspaceDescription && (
                <p className="text-xs text-slate-500">
                  {workspaceDescription}
                </p>
              )}
            </div>
          </div>

          {/* ================= CURRENT PROJECT ================= */}

          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Current Project
            </p>

            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-800">
                {project?.projectName ||
                  project?.project_name ||
                  "Not assigned"}
              </h3>

              <span className="text-xs text-slate-400">
                {project?.projectCode ||
                  project?.project_code ||
                  ""}
              </span>
            </div>
          </div>

          {/* ================= WORKSPACE INFO ================= */}

          <div className="mt-3 grid grid-cols-4 gap-3">

            {/* Members */}

            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-slate-500">
                <Users size={14} />

                <span className="text-[10px] uppercase">
                  Members
                </span>
              </div>

              <p className="mt-1 text-sm font-semibold text-slate-800">
                {memberCount}
              </p>
            </div>

            {/* Progress */}

            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-slate-500">
                <TrendingUp size={14} />

                <span className="text-[10px] uppercase">
                  Progress
                </span>
              </div>

              <p className="mt-1 text-sm font-semibold text-slate-800">
                {progress}%
              </p>
            </div>

            {/* Status */}

            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <p className="text-[10px] uppercase text-slate-500">
                Status
              </p>

              <span
                className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                  statusStyles[workspaceStatus] ||
                  "bg-slate-100 text-slate-700"
                }`}
              >
                {workspaceStatus}
              </span>
            </div>

            {/* Workspace Code */}

            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <p className="text-[10px] uppercase text-slate-500">
                Code
              </p>

              <p className="mt-1 truncate text-sm font-semibold text-slate-800">
                {workspaceCode}
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceHeader;